import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { X, Save, Edit3, AlertCircle } from 'lucide-react';

const CollegeDataEditor = ({ college, onClose, onSave, isGlobalEdit = false }) => {
  const [formData, setFormData] = useState({
    // College ID for updates
    id: '',
    // Basic Info
    name: '',
    official_name: '',
    city: '',
    state: '',
    type: '',
    website: '',
    academic_focus: '',
    
    // Rankings
    ranking_type: 'national_university',
    usnwr_ranking: '',
    liberal_arts_ranking: '',
    engineering_ranking: '',
    nursing_ranking: '',
    business_ranking: '',
    computer_science_ranking: '',
    
    // Admissions Data
    acceptance_rate: '',
    test_optional: false,
    test_blind: false,
    sat_range: '',
    act_range: '',
    
    // Financial Information
    tuition: '',
    tuition_in_state: '',
    tuition_out_of_state: '',
    room_board: '',
    total_cost: '',
    need_blind: false,
    avg_merit_aid: '',
    
    // Application Requirements
    num_essays: '',
    essay_prompts: '',
    requires_counselor_rec: true,
    requires_teacher_rec: true,
    num_teacher_recs_required: '2',
    interview_required: false,
    interview_optional: false,
    
    // Application Deadlines
    ed_deadline: '',
    ed2_deadline: '',
    ea_deadline: '',
    rd_deadline: '',
    finaid_deadline: '',
    css_deadline: '',
    merit_deadline: '',
    honors_deadline: '',
    
    // Student-Specific Fields
    category: 'target',
    status: 'researching',
    notes: '',
    student_advantages: '',
    application_strategy: '',
    why_interested: '',
    major_of_interest: '',
    questions_for_college: '',
    
    // Other
    student_enrollment: '',
    aliases: []
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingGlobalData, setLoadingGlobalData] = useState(false);

  useEffect(() => {
    const loadCollegeData = async () => {
      if (!college) return;
      
      // If we have a global_college_id, fetch the full data from global_colleges
      if (college.global_college_id && !isGlobalEdit) {
        setLoadingGlobalData(true);
        try {
          console.log('🔍 Fetching global college data for ID:', college.global_college_id);
          const { data: globalData, error } = await supabase
            .from('global_colleges')
            .select('*')
            .eq('id', college.global_college_id)
            .single();
          
          if (error) {
            console.error('Error fetching global college data:', error);
            toast.error('Failed to load college data');
          } else if (globalData) {
            console.log('✅ Loaded global college data:', globalData.name);
            // Merge global data with student-specific data
            setFormData({
              // Preserve college ID for updates
              id: college.id || '',
              // Basic Info from global_colleges
              name: globalData.name || college.name || '',
              official_name: globalData.official_name || '',
              city: globalData.city || '',
              state: globalData.state || '',
              type: globalData.type || '',
              website: globalData.website || '',
              academic_focus: globalData.academic_focus || '',
              
              // Rankings from global_colleges
              ranking_type: globalData.ranking_type || 'national_university',
              usnwr_ranking: globalData.usnwr_ranking || '',
              liberal_arts_ranking: globalData.liberal_arts_ranking || '',
              engineering_ranking: globalData.engineering_ranking || '',
              nursing_ranking: globalData.nursing_ranking || '',
              business_ranking: globalData.business_ranking || '',
              computer_science_ranking: globalData.computer_science_ranking || '',
              
              // Admissions Data from global_colleges
              acceptance_rate: globalData.acceptance_rate || '',
              test_optional: globalData.test_optional || false,
              test_blind: globalData.test_blind || false,
              sat_range: globalData.sat_range || '',
              act_range: globalData.act_range || '',
              
              // Financial Information from global_colleges
              tuition: globalData.tuition || '',
              tuition_in_state: globalData.tuition_in_state || '',
              tuition_out_of_state: globalData.tuition_out_of_state || '',
              room_board: globalData.room_board || '',
              total_cost: globalData.total_cost || '',
              need_blind: globalData.need_blind || false,
              avg_merit_aid: globalData.avg_merit_aid || '',
              
              // Application Requirements from global_colleges
              num_essays: globalData.num_essays || globalData.supplement_count || '',
              essay_prompts: globalData.essay_prompts || '',
              requires_counselor_rec: globalData.requires_counselor_rec !== false,
              requires_teacher_rec: globalData.requires_teacher_rec !== false,
              num_teacher_recs_required: globalData.num_teacher_recs_required || '2',
              interview_required: globalData.interview_required || false,
              interview_optional: globalData.interview_optional || false,
              
              // Application Deadlines from global_colleges
              ed_deadline: globalData.ed_deadline || '',
              ed2_deadline: globalData.ed2_deadline || '',
              ea_deadline: globalData.ea_deadline || '',
              rd_deadline: globalData.rd_deadline || '',
              finaid_deadline: globalData.finaid_deadline || '',
              css_deadline: globalData.css_deadline || '',
              merit_deadline: globalData.merit_deadline || '',
              honors_deadline: globalData.honors_deadline || '',
              
              // Student-Specific Fields from college_journey_colleges
              category: college.category || 'target',
              status: college.status || 'researching',
              notes: college.notes || '',
              student_advantages: college.student_advantages || '',
              application_strategy: college.application_strategy || '',
              why_interested: college.why_interested || '',
              major_of_interest: college.major_of_interest || '',
              questions_for_college: college.questions_for_college || '',
              
              // Other from global_colleges
              student_enrollment: globalData.student_enrollment || '',
              aliases: globalData.aliases || []
            });
          }
        } catch (error) {
          console.error('Error loading global college data:', error);
          toast.error('Failed to load college data');
        } finally {
          setLoadingGlobalData(false);
        }
      } else {
        // Fallback to using the college object directly (for global edit or if no global_college_id)
        setFormData({
          // Preserve college ID for updates
          id: college.id || '',
          // Basic Info
          name: college.name || '',
          official_name: college.official_name || '',
          city: college.city || '',
          state: college.state || '',
          type: college.type || '',
          website: college.website || '',
          academic_focus: college.academic_focus || '',
          
          // Rankings
          ranking_type: college.ranking_type || 'national_university',
          usnwr_ranking: college.usnwr_ranking || '',
          liberal_arts_ranking: college.liberal_arts_ranking || '',
          engineering_ranking: college.engineering_ranking || '',
          nursing_ranking: college.nursing_ranking || '',
          business_ranking: college.business_ranking || '',
          computer_science_ranking: college.computer_science_ranking || '',
          
          // Admissions Data
          acceptance_rate: college.acceptance_rate || '',
          test_optional: college.test_optional || false,
          test_blind: college.test_blind || false,
          sat_range: college.sat_range || '',
          act_range: college.act_range || '',
          
          // Financial Information
          tuition: college.tuition || '',
          tuition_in_state: college.tuition_in_state || '',
          tuition_out_of_state: college.tuition_out_of_state || '',
          room_board: college.room_board || '',
          total_cost: college.total_cost || '',
          need_blind: college.need_blind || false,
          avg_merit_aid: college.avg_merit_aid || '',
          
          // Application Requirements
          num_essays: college.num_essays || '',
          essay_prompts: college.essay_prompts || '',
          requires_counselor_rec: college.requires_counselor_rec !== false,
          requires_teacher_rec: college.requires_teacher_rec !== false,
          num_teacher_recs_required: college.num_teacher_recs_required || '2',
          interview_required: college.interview_required || false,
          interview_optional: college.interview_optional || false,
          
          // Application Deadlines
          ed_deadline: college.ed_deadline || '',
          ed2_deadline: college.ed2_deadline || '',
          ea_deadline: college.ea_deadline || '',
          rd_deadline: college.rd_deadline || '',
          finaid_deadline: college.finaid_deadline || '',
          css_deadline: college.css_deadline || '',
          merit_deadline: college.merit_deadline || '',
          honors_deadline: college.honors_deadline || '',
          
          // Student-Specific Fields
          category: college.category || 'target',
          status: college.status || 'researching',
          notes: college.notes || '',
          student_advantages: college.student_advantages || '',
          application_strategy: college.application_strategy || '',
          why_interested: college.why_interested || '',
          major_of_interest: college.major_of_interest || '',
          questions_for_college: college.questions_for_college || '',
          
          // Other
          student_enrollment: college.student_enrollment || '',
          aliases: college.aliases || []
        });
      }
    };
    
    loadCollegeData();
  }, [college, isGlobalEdit]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAliasAdd = () => {
    const newAlias = prompt('Enter a new alias for this college:');
    if (newAlias) {
      setFormData(prev => ({
        ...prev,
        aliases: [...prev.aliases, newAlias]
      }));
    }
  };

  const handleAliasRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      aliases: prev.aliases.filter((_, i) => i !== index)
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name) {
      newErrors.name = 'College name is required';
    }
    
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    
    // Validate numeric fields
    const numericFields = [
      'usnwr_ranking', 'liberal_arts_ranking', 'engineering_ranking', 
      'nursing_ranking', 'business_ranking', 'computer_science_ranking',
      'acceptance_rate', 'tuition', 'tuition_in_state', 'tuition_out_of_state',
      'room_board', 'total_cost', 'avg_merit_aid', 'student_enrollment', 'num_essays'
    ];
    
    numericFields.forEach(field => {
      if (formData[field] && isNaN(formData[field])) {
        newErrors[field] = 'Must be a number';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    
    setSaving(true);
    
    try {
      
      // If this is a student's college (from college_journey_colleges), pass all form data
      // The service layer will filter to only save student-specific fields
      if (!isGlobalEdit && college?.global_college_id) {
        // Clean up numeric and date fields before passing to onSave
        const cleanedData = {
          ...formData,
          // Convert empty strings to null for numeric fields
          usnwr_ranking: formData.usnwr_ranking === '' ? null : formData.usnwr_ranking,
          liberal_arts_ranking: formData.liberal_arts_ranking === '' ? null : formData.liberal_arts_ranking,
          engineering_ranking: formData.engineering_ranking === '' ? null : formData.engineering_ranking,
          nursing_ranking: formData.nursing_ranking === '' ? null : formData.nursing_ranking,
          business_ranking: formData.business_ranking === '' ? null : formData.business_ranking,
          computer_science_ranking: formData.computer_science_ranking === '' ? null : formData.computer_science_ranking,
          acceptance_rate: formData.acceptance_rate === '' ? null : formData.acceptance_rate,
          tuition: formData.tuition === '' ? null : formData.tuition,
          tuition_in_state: formData.tuition_in_state === '' ? null : formData.tuition_in_state,
          tuition_out_of_state: formData.tuition_out_of_state === '' ? null : formData.tuition_out_of_state,
          room_board: formData.room_board === '' ? null : formData.room_board,
          total_cost: formData.total_cost === '' ? null : formData.total_cost,
          avg_merit_aid: formData.avg_merit_aid === '' ? null : formData.avg_merit_aid,
          student_enrollment: formData.student_enrollment === '' ? null : formData.student_enrollment,
          num_essays: formData.num_essays === '' ? null : formData.num_essays,
          num_teacher_recs_required: formData.num_teacher_recs_required === '' ? null : formData.num_teacher_recs_required,
          // Convert empty strings to null for date fields
          ed_deadline: formData.ed_deadline === '' ? null : formData.ed_deadline,
          ed2_deadline: formData.ed2_deadline === '' ? null : formData.ed2_deadline,
          ea_deadline: formData.ea_deadline === '' ? null : formData.ea_deadline,
          rd_deadline: formData.rd_deadline === '' ? null : formData.rd_deadline,
          finaid_deadline: formData.finaid_deadline === '' ? null : formData.finaid_deadline,
          css_deadline: formData.css_deadline === '' ? null : formData.css_deadline,
          merit_deadline: formData.merit_deadline === '' ? null : formData.merit_deadline,
          honors_deadline: formData.honors_deadline === '' ? null : formData.honors_deadline,
        };
        
        try {
          await onSave?.(cleanedData);
          setSaving(false);
          // Success - onSave in parent should show toast, then we close
          // Add a small delay to ensure toast shows before modal closes
          setTimeout(() => {
            onClose();
          }, 100);
        } catch (error) {
          console.error('Error saving college data:', error);
          setSaving(false);
          // Don't close on error
        }
        return;
      }
      
      // For global colleges (admin edit), prepare all data
      const saveData = {
        ...formData,
        // Convert string numbers to actual numbers or null
        usnwr_ranking: formData.usnwr_ranking ? parseInt(formData.usnwr_ranking) : null,
        liberal_arts_ranking: formData.liberal_arts_ranking ? parseInt(formData.liberal_arts_ranking) : null,
        engineering_ranking: formData.engineering_ranking ? parseInt(formData.engineering_ranking) : null,
        nursing_ranking: formData.nursing_ranking ? parseInt(formData.nursing_ranking) : null,
        business_ranking: formData.business_ranking ? parseInt(formData.business_ranking) : null,
        computer_science_ranking: formData.computer_science_ranking ? parseInt(formData.computer_science_ranking) : null,
        acceptance_rate: formData.acceptance_rate ? parseFloat(formData.acceptance_rate) : null,
        tuition: formData.tuition ? parseInt(formData.tuition) : null,
        tuition_in_state: formData.tuition_in_state ? parseInt(formData.tuition_in_state) : null,
        tuition_out_of_state: formData.tuition_out_of_state ? parseInt(formData.tuition_out_of_state) : null,
        room_board: formData.room_board ? parseInt(formData.room_board) : null,
        total_cost: formData.total_cost ? parseInt(formData.total_cost) : null,
        avg_merit_aid: formData.avg_merit_aid ? parseInt(formData.avg_merit_aid) : null,
        student_enrollment: formData.student_enrollment ? parseInt(formData.student_enrollment) : null,
        num_essays: formData.num_essays ? parseInt(formData.num_essays) : null,
        num_teacher_recs_required: formData.num_teacher_recs_required ? parseInt(formData.num_teacher_recs_required) : null,
        // Convert empty strings to null for date fields
        ed_deadline: formData.ed_deadline || null,
        ed2_deadline: formData.ed2_deadline || null,
        ea_deadline: formData.ea_deadline || null,
        rd_deadline: formData.rd_deadline || null,
        finaid_deadline: formData.finaid_deadline || null,
        css_deadline: formData.css_deadline || null,
        merit_deadline: formData.merit_deadline || null,
        honors_deadline: formData.honors_deadline || null,
        updated_at: new Date().toISOString()
      };
      
      // Remove fields that don't exist in the target table
      if (!isGlobalEdit) {
        // Remove fields that don't exist in college_journey_colleges
        delete saveData.ed_deadline_source;
        delete saveData.ed2_deadline_source;
        delete saveData.ea_deadline_source;
        delete saveData.rd_deadline_source;
        delete saveData.finaid_deadline_source;
        delete saveData.css_deadline_source;
        delete saveData.merit_deadline_source;
        delete saveData.honors_deadline_source;
      }
      
      onSave?.(saveData);
      onClose();
    } catch (error) {
      console.error('Error saving college:', error);
      toast.error('Failed to save college');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Loading Indicator */}
        {loadingGlobalData && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
            zIndex: 1
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '3px solid #e5e7eb',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              <p style={{ color: '#6b7280' }}>Loading college data...</p>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Edit3 size={24} style={{ color: '#6366f1' }} />
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
              {college?.id ? 'Edit College' : 'Add New College'}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '24px'
        }}>
          {/* Basic Information */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              Basic Information
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  College Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: errors.name ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {errors.name && (
                  <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.name}</p>
                )}
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Official Name
                </label>
                <input
                  type="text"
                  value={formData.official_name || ''}
                  onChange={(e) => handleChange('official_name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  City
                </label>
                <input
                  type="text"
                  value={formData.city || ''}
                  onChange={(e) => handleChange('city', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  State *
                </label>
                <input
                  type="text"
                  value={formData.state || ''}
                  onChange={(e) => handleChange('state', e.target.value)}
                  maxLength={2}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: errors.state ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {errors.state && (
                  <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.state}</p>
                )}
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Type
                </label>
                <select
                  value={formData.type || ''}
                  onChange={(e) => handleChange('type', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select Type</option>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Institution Type
                </label>
                <select
                  value={formData.ranking_type || 'national_university'}
                  onChange={(e) => handleChange('ranking_type', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="national_university">National University</option>
                  <option value="liberal_arts_college">Liberal Arts College</option>
                </select>
              </div>
            </div>
          </div>

          {/* Rankings */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              Rankings
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              {formData.ranking_type === 'national_university' ? (
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                    USNWR National Universities
                  </label>
                  <input
                    type="number"
                    value={formData.usnwr_ranking || ''}
                    onChange={(e) => handleChange('usnwr_ranking', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              ) : (
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                    USNWR Liberal Arts Colleges
                  </label>
                  <input
                    type="number"
                    value={formData.liberal_arts_ranking || ''}
                    onChange={(e) => handleChange('liberal_arts_ranking', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Engineering Ranking
                </label>
                <input
                  type="number"
                  value={formData.engineering_ranking || ''}
                  onChange={(e) => handleChange('engineering_ranking', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Nursing Ranking
                </label>
                <input
                  type="number"
                  value={formData.nursing_ranking || ''}
                  onChange={(e) => handleChange('nursing_ranking', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Academic & Financial */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              Academic & Financial
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Acceptance Rate (%)
                </label>
                <input
                  type="number"
                  value={formData.acceptance_rate || ''}
                  onChange={(e) => handleChange('acceptance_rate', e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  In-State Tuition ($)
                </label>
                <input
                  type="number"
                  value={formData.tuition_in_state || ''}
                  onChange={(e) => handleChange('tuition_in_state', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Out-of-State Tuition ($)
                </label>
                <input
                  type="number"
                  value={formData.tuition_out_of_state || ''}
                  onChange={(e) => handleChange('tuition_out_of_state', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Student Enrollment
                </label>
                <input
                  type="number"
                  value={formData.student_enrollment || ''}
                  onChange={(e) => handleChange('student_enrollment', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Academic Focus
                </label>
                <input
                  type="text"
                  value={formData.academic_focus || ''}
                  onChange={(e) => handleChange('academic_focus', e.target.value)}
                  placeholder="e.g., Engineering, Nursing, Liberal Arts"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            
            <div style={{ marginTop: '16px', display: 'flex', gap: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.test_optional || false}
                  onChange={(e) => handleChange('test_optional', e.target.checked)}
                />
                <span style={{ fontSize: '14px' }}>Test Optional</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.test_blind || false}
                  onChange={(e) => handleChange('test_blind', e.target.checked)}
                />
                <span style={{ fontSize: '14px' }}>Test Blind</span>
              </label>
            </div>
          </div>

          {/* Aliases */}
          {isGlobalEdit && (
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                Aliases
              </h3>
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                {formData.aliases.map((alias, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '4px 12px',
                      background: '#eff6ff',
                      border: '1px solid #3b82f6',
                      borderRadius: '16px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {alias}
                    <button
                      onClick={() => handleAliasRemove(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              
              <button
                onClick={handleAliasAdd}
                style={{
                  padding: '8px 16px',
                  background: '#eff6ff',
                  color: '#3b82f6',
                  border: '1px solid #3b82f6',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Add Alias
            </button>
          </div>
          )}
          
          {/* Student-Specific Fields (only show for student colleges) */}
          {!isGlobalEdit && (
            <>
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                  My Application Strategy
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                      Category
                    </label>
                    <select
                      value={formData.category || 'target'}
                      onChange={(e) => handleChange('category', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="reach">Reach School</option>
                      <option value="target">Target School</option>
                      <option value="safety">Safety School</option>
                      <option value="other">Additional/Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                      Application Status
                    </label>
                    <select
                      value={formData.status || 'researching'}
                      onChange={(e) => handleChange('status', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="researching">Researching</option>
                      <option value="interested">Interested</option>
                      <option value="applying">Applying</option>
                      <option value="applied">Applied</option>
                      <option value="accepted">Accepted</option>
                      <option value="denied">Denied</option>
                      <option value="waitlisted">Waitlisted</option>
                      <option value="committed">Committed</option>
                    </select>
                  </div>
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                    Why I'm Interested
                  </label>
                  <textarea
                    value={formData.why_interested || ''}
                    onChange={(e) => handleChange('why_interested', e.target.value)}
                    rows={3}
                    placeholder="What attracts you to this college?"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                    My Strategic Advantages
                  </label>
                  <textarea
                    value={formData.student_advantages || ''}
                    onChange={(e) => handleChange('student_advantages', e.target.value)}
                    rows={4}
                    placeholder="What unique advantages do I have for this school?"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                    Application Strategy Notes
                  </label>
                  <textarea
                    value={formData.application_strategy || ''}
                    onChange={(e) => handleChange('application_strategy', e.target.value)}
                    rows={3}
                    placeholder="Application strategy, timing, essay approach, etc."
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
              
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                  Research & Notes
                </h3>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                    Major of Interest
                  </label>
                  <input
                    type="text"
                    value={formData.major_of_interest || ''}
                    onChange={(e) => handleChange('major_of_interest', e.target.value)}
                    placeholder="e.g., Computer Science, Engineering, Business"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                    Questions for College
                  </label>
                  <textarea
                    value={formData.questions_for_college || ''}
                    onChange={(e) => handleChange('questions_for_college', e.target.value)}
                    rows={3}
                    placeholder="Questions to ask during visits, interviews, or info sessions"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>
                

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                    General Notes
                  </label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    rows={3}
                    placeholder="Any other notes about this college"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Additional Financial Information */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              Additional Financial Information
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Room & Board
                </label>
                <input
                  type="number"
                  value={formData.room_board || ''}
                  onChange={(e) => handleChange('room_board', e.target.value)}
                  placeholder="Annual cost"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Total Cost
                </label>
                <input
                  type="number"
                  value={formData.total_cost || ''}
                  onChange={(e) => handleChange('total_cost', e.target.value)}
                  placeholder="Total annual cost"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Average Merit Aid
                </label>
                <input
                  type="number"
                  value={formData.avg_merit_aid || ''}
                  onChange={(e) => handleChange('avg_merit_aid', e.target.value)}
                  placeholder="Average amount"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            
            <div style={{ marginTop: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.need_blind || false}
                  onChange={(e) => handleChange('need_blind', e.target.checked)}
                />
                <span style={{ fontSize: '14px' }}>Need-Blind Admissions</span>
              </label>
            </div>
          </div>

          {/* Test Scores */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              Test Score Ranges
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  SAT Range
                </label>
                <input
                  type="text"
                  value={formData.sat_range || ''}
                  onChange={(e) => handleChange('sat_range', e.target.value)}
                  placeholder="e.g., 1450-1570"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  ACT Range
                </label>
                <input
                  type="text"
                  value={formData.act_range || ''}
                  onChange={(e) => handleChange('act_range', e.target.value)}
                  placeholder="e.g., 33-35"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Application Requirements */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              Application Requirements
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Number of Essays
                </label>
                <input
                  type="number"
                  value={formData.num_essays || ''}
                  onChange={(e) => handleChange('num_essays', e.target.value)}
                  placeholder="Total required essays"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Teacher Recommendations Required
                </label>
                <select
                  value={formData.num_teacher_recs_required || '2'}
                  onChange={(e) => handleChange('num_teacher_recs_required', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="0">None</option>
                  <option value="1">1 Recommendation</option>
                  <option value="2">2 Recommendations</option>
                  <option value="3">3 Recommendations</option>
                </select>
              </div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                Essay Prompts
              </label>
              <textarea
                value={formData.essay_prompts || ''}
                onChange={(e) => handleChange('essay_prompts', e.target.value)}
                placeholder="List of essay prompts (one per line)"
                rows={4}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.requires_counselor_rec}
                  onChange={(e) => handleChange('requires_counselor_rec', e.target.checked)}
                />
                <span style={{ fontSize: '14px' }}>Counselor Recommendation Required</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.requires_teacher_rec}
                  onChange={(e) => handleChange('requires_teacher_rec', e.target.checked)}
                />
                <span style={{ fontSize: '14px' }}>Teacher Recommendation Required</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.interview_required || false}
                  onChange={(e) => handleChange('interview_required', e.target.checked)}
                />
                <span style={{ fontSize: '14px' }}>Interview Required</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.interview_optional || false}
                  onChange={(e) => handleChange('interview_optional', e.target.checked)}
                />
                <span style={{ fontSize: '14px' }}>Interview Optional</span>
              </label>
            </div>
          </div>

          {/* Application Deadlines */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              Application Deadlines
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Early Decision
                </label>
                <input
                  type="date"
                  value={formData.ed_deadline || ''}
                  onChange={(e) => handleChange('ed_deadline', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Early Decision 2
                </label>
                <input
                  type="date"
                  value={formData.ed2_deadline || ''}
                  onChange={(e) => handleChange('ed2_deadline', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Early Action
                </label>
                <input
                  type="date"
                  value={formData.ea_deadline || ''}
                  onChange={(e) => handleChange('ea_deadline', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Regular Decision
                </label>
                <input
                  type="date"
                  value={formData.rd_deadline || ''}
                  onChange={(e) => handleChange('rd_deadline', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Financial Aid
                </label>
                <input
                  type="date"
                  value={formData.finaid_deadline || ''}
                  onChange={(e) => handleChange('finaid_deadline', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  CSS Profile
                </label>
                <input
                  type="date"
                  value={formData.css_deadline || ''}
                  onChange={(e) => handleChange('css_deadline', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Merit Scholarships
                </label>
                <input
                  type="date"
                  value={formData.merit_deadline || ''}
                  onChange={(e) => handleChange('merit_deadline', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                  Honors Program
                </label>
                <input
                  type="date"
                  value={formData.honors_deadline || ''}
                  onChange={(e) => handleChange('honors_deadline', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            {Object.keys(errors).length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}>
                <AlertCircle size={16} />
                <span style={{ fontSize: '14px' }}>Please fix the errors above</span>
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '8px 16px',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save College'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default CollegeDataEditor;